import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../lib/api';
import { useAnalyticsAccess } from '../../hooks/useAnalyticsAccess';

interface ActiveUser {
  id: number;
  name: string;
  email: string;
  userType: string;
  profilePicture?: string;
  country?: string;
  lastActive: string;
  sessionCount: number;
}

interface ActiveUsersResponse {
  success: boolean;
  count: number;
  users: ActiveUser[];
}

export default function WhosOnline() {
  const { hasAnalyticsAccess, accessReason } = useAnalyticsAccess();

  // Fetch active users, refresh every 30 seconds
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['activeUsers'],
    queryFn: async () => {
      return await apiService.request<ActiveUsersResponse>('/api/v1/admin/active-users');
    },
    refetchInterval: 30000, // Refresh every 30 seconds
    staleTime: 15000, // Consider data stale after 15 seconds
  });

  if (!hasAnalyticsAccess) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <h3 className="text-lg font-medium text-yellow-800 dark:text-yellow-200 mb-2">
            Access Restricted
          </h3>
          <p className="text-yellow-700 dark:text-yellow-300">
            {accessReason}
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-6"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="theme-card shadow-sm p-8 text-center">
          <div className="text-red-500 mb-4">
            <svg className="h-12 w-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold theme-text mb-2">Failed to Load Active Users</h3>
          <p className="theme-text-muted mb-4">{(error as Error).message}</p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const activeUsers = data?.users || [];
  const userCount = data?.count || 0;

  // Helper to format time ago
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="theme-card shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold theme-text">Who's Online</h1>
              <p className="text-sm theme-text-muted">
                {userCount} {userCount === 1 ? 'user' : 'users'} active in the last 15 minutes
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm theme-text-muted">Live</span>
            </div>
          </div>
        </div>
      </div>

      {/* Active Users List */}
      <div className="theme-card shadow-sm p-6">
        {activeUsers.length === 0 ? (
          <div className="text-center py-12 theme-text-muted">
            <svg className="h-16 w-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <p className="text-lg">No users currently active</p>
            <p className="text-sm mt-2">Check back soon!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {activeUsers.map((user) => (
              <div
                key={user.id}
                className="flex items-center space-x-4 p-4 theme-surface-elevated rounded-lg theme-hover transition-colors"
              >
                {/* Avatar */}
                <div className="flex-shrink-0">
                  {user.profilePicture ? (
                    <img
                      src={user.profilePicture}
                      alt={user.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold"
                      style={{
                        background: 'linear-gradient(135deg, hsl(142, 76%, 36%), hsl(217, 91%, 60%))'
                      }}
                    >
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>

                {/* User Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <h3 className="font-medium theme-text text-sm truncate">{user.name}</h3>
                    <div className="flex-shrink-0 flex items-center space-x-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-xs text-green-600 dark:text-green-400">Online</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 text-xs theme-text-muted mt-1">
                    <span className="truncate">{user.email}</span>
                    {user.country && (
                      <>
                        <span>•</span>
                        <span>{user.country}</span>
                      </>
                    )}
                    {user.sessionCount > 1 && (
                      <>
                        <span>•</span>
                        <span>{user.sessionCount} devices</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Last Active */}
                <div className="flex-shrink-0 text-right">
                  <div className="text-xs theme-text-muted">
                    {formatTimeAgo(user.lastActive)}
                  </div>
                  {user.userType && (
                    <div className="mt-1">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                        {user.userType}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Footer */}
      <div className="text-center text-xs theme-text-muted">
        <p>Updates automatically every 30 seconds</p>
        <p className="mt-1">Shows users who have been active in the last 15 minutes</p>
      </div>
    </div>
  );
}
